import type { TopicPollingConfig } from "./types";

export interface PollingFallbackManagerOptions {
  connectionName: string;
  pollingConfigs: TopicPollingConfig[];
  logger: (...args: unknown[]) => void;
  /** Called to deliver a polled item — main class routes to topic callbacks */
  onEvent: (topic: string, data: unknown) => void;
}

/**
 * Manages the polling fallback mechanism for when SSE connections fail.
 * Polls configured topics at regular intervals and delivers items
 * via onEvent callback. Deduplication is the responsibility of each
 * topic's pollingFn (e.g. CursorPoller tracks its own cursor).
 */
export class PollingFallbackManager {
  /** Per-topic polling configurations */
  readonly pollingConfigMap: Map<string, TopicPollingConfig> = new Map();

  /** Active polling interval timers per topic */
  private pollingTimers: Map<string, ReturnType<typeof setInterval>> =
    new Map();

  /** Persistent flag — survives destroy() so remounting widgets stay in polling mode */
  private _isInPollingMode = false;

  private readonly connectionName: string;
  private readonly logger: (...args: unknown[]) => void;
  private readonly onEvent: (topic: string, data: unknown) => void;

  constructor(options: PollingFallbackManagerOptions) {
    this.connectionName = options.connectionName;
    this.logger = options.logger;
    this.onEvent = options.onEvent;

    for (const pc of options.pollingConfigs) {
      this.pollingConfigMap.set(pc.topic, pc);
    }
  }

  /** Whether polling mode is active — persists across widget unmount/remount cycles */
  get isPollingMode(): boolean {
    return this._isInPollingMode;
  }

  /** Activate polling mode. Persists until page reload or SSE recovery. */
  activate(): void {
    this._isInPollingMode = true;
  }

  /** Exit polling mode. Called when SSE recovery succeeds. */
  deactivate(): void {
    this._isInPollingMode = false;
    this.stopAllPolling();
  }

  /**
   * Start polling for a single topic. Runs the first poll immediately,
   * then starts a recurring interval.
   */
  startTopicPolling(topic: string, config: TopicPollingConfig): void {
    if (this.pollingTimers.has(topic)) {
      return; // Already polling this topic
    }

    const intervalMs = config.intervalMs ?? 10000;

    this.logger(
      `[${this.connectionName}] Starting polling for topic "${topic}" every ${intervalMs / 1000}s`
    );

    // Run first poll immediately
    this.executePoll(topic, config);

    // Start interval
    const timer = setInterval(() => {
      this.executePoll(topic, config);
    }, intervalMs);

    this.pollingTimers.set(topic, timer);
  }

  /**
   * Stop polling for a single topic.
   */
  stopTopicPolling(topic: string): void {
    const timer = this.pollingTimers.get(topic);
    if (timer) {
      clearInterval(timer);
      this.pollingTimers.delete(topic);
    }
  }

  /**
   * Full cleanup — stops all polling.
   */
  destroy(): void {
    this.stopAllPolling();
  }

  /**
   * Execute a single poll for a topic.
   * Delivers all items returned by the pollingFn — deduplication is the
   * pollingFn's responsibility (e.g. CursorPoller tracks its own cursor).
   */
  private async executePoll(
    topic: string,
    config: TopicPollingConfig
  ): Promise<void> {
    try {
      const items = await config.pollingFn();

      // Check we're still in polling mode after the async call
      if (!this.isPollingMode) return;

      if (items.length > 0) {
        this.logger(
          `[${this.connectionName}] Poll delivered ${items.length} item(s) for topic "${topic}"`
        );

        for (const item of items) {
          this.onEvent(topic, item);
        }
      }
    } catch (error) {
      // Polling errors are non-fatal — log and retry on next interval
      this.logger(
        `[${this.connectionName}] Poll failed for topic "${topic}":`,
        error
      );
    }
  }

  /**
   * Stop all polling.
   */
  private stopAllPolling(): void {
    for (const timer of this.pollingTimers.values()) {
      clearInterval(timer);
    }
    this.pollingTimers.clear();
  }
}
